// ============================================
// Skeletons.js — Placeholders de carga (tarjetas fantasma con shimmer)
// ============================================
// RENDIMIENTO Paso 1: mientras cargan los datos, en vez de texto "Cargando..." se
// muestran tarjetas/filas FANTASMA con la forma del contenido real + animacion shimmer
// suave. Mejora la sensacion de carga (la pantalla no se ve en blanco).
//
// Reusa el MISMO estilo de skeleton de La Tienda (gradiente gris + shimmer), pero como
// componente autonomo (inyecta su propia clase `.beep-skeleton` + keyframes) para no
// depender del <style> embebido de Tienda.js y poder usarlo en Colecciones / Listas.
//
// RESPONSIVE: los grids usan `repeat(auto-fill, minmax(...))` con el MISMO minimo/gap
// que el contenido real -> colapsan igual en celular/tablet/PC (vertical u horizontal)
// y anchos intermedios. Para Listas, el skeleton respeta el switch tabla<->tarjetas via
// la prop `compacto` (mismo umbral que el contenido).

import React from 'react';

const COLOR_BORDE = '#e0e3e6';

// Inyecta la clase shimmer + keyframes. Repetirlo (varias instancias) es inocuo: el
// navegador deduplica reglas identicas. Respeta prefers-reduced-motion.
function EstilosSkeleton() {
  return (
    <style>{`
      .beep-skeleton {
        background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
        background-size: 200% 100%;
        animation: beepShimmer 1.5s infinite;
      }
      @keyframes beepShimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @media (prefers-reduced-motion: reduce) {
        .beep-skeleton { animation-duration: 0.01ms !important; }
      }
    `}</style>
  );
}

// Bloque gris base (un trozo de skeleton).
function Bloque({ style }) {
  return <div className="beep-skeleton" style={{ borderRadius: 6, ...style }} />;
}

// Grid auto-fill identico al del contenido (colapsa por ancho). Reusable.
function Grid({ min, gap, children }) {
  return (
    <div style={{ display: 'grid', gap, gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${min}px), 1fr))` }}>
      {children}
    </div>
  );
}

// ============================================
// PRODUCTOS — tarjeta fantasma con forma de TarjetaProducto (foto + lineas + boton).
// Para La Tienda (no usado aqui, ya tiene el suyo) y el DETALLE de Colecciones/Listas.
// Mismo grid que el detalle: minmax(230px,1fr), gap 16.
// ============================================
export function SkeletonTarjetasProducto({ count = 8, min = 230, gap = 16 }) {
  return (
    <>
      <EstilosSkeleton />
      <Grid min={min} gap={gap}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} style={{
            background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 12,
            padding: 12, display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            <Bloque style={{ height: 140, borderRadius: 8 }} />
            <Bloque style={{ height: 12, width: '40%' }} />
            <Bloque style={{ height: 16 }} />
            <Bloque style={{ height: 16, width: '70%' }} />
            <Bloque style={{ height: 22, width: '50%', marginTop: 'auto' }} />
            <Bloque style={{ height: 38, borderRadius: 8 }} />
          </div>
        ))}
      </Grid>
    </>
  );
}

// ============================================
// COLECCIONES — tarjeta fantasma con forma de TarjetaColeccion (icono + titulo + stats
// + acciones). Mismo grid que la lista: minmax(280px,1fr), gap 12.
// ============================================
export function SkeletonTarjetasColeccion({ count = 6, min = 280, gap = 12 }) {
  return (
    <>
      <EstilosSkeleton />
      <Grid min={min} gap={gap}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} style={{
            background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 12,
            padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Bloque style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Bloque style={{ height: 15, width: '70%' }} />
                <Bloque style={{ height: 11, width: '90%' }} />
              </div>
            </div>
            <Bloque style={{ height: 12, width: '50%' }} />
            <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
              <Bloque style={{ height: 36, flex: 1, borderRadius: 8 }} />
              <Bloque style={{ width: 36, height: 36, borderRadius: 8 }} />
            </div>
          </div>
        ))}
      </Grid>
    </>
  );
}

// ============================================
// LISTAS PACTADAS — tarjetas fantasma con la forma de TarjetaListaCongelada (mismo grid
// que Colecciones: minmax(280px,1fr), gap 12). Siempre tarjetas (igual que el contenido).
// ============================================
export function SkeletonListasCongeladas({ count = 6, min = 280, gap = 12 }) {
  return (
    <>
      <EstilosSkeleton />
      <Grid min={min} gap={gap}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} style={{
            background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 12,
            padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Bloque style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Bloque style={{ height: 15, width: '70%' }} />
                <Bloque style={{ height: 11, width: '90%' }} />
              </div>
            </div>
            <Bloque style={{ height: 18, width: 110, borderRadius: 20 }} />
            <Bloque style={{ height: 12, width: '60%' }} />
            <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
              <Bloque style={{ height: 36, flex: 1, borderRadius: 8 }} />
              <Bloque style={{ width: 36, height: 36, borderRadius: 8 }} />
              <Bloque style={{ width: 36, height: 36, borderRadius: 8 }} />
            </div>
          </div>
        ))}
      </Grid>
    </>
  );
}
